import { FunctionComponent, useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { useTranslation } from "react-i18next";

import { faCircleExclamation } from "@fortawesome/pro-regular-svg-icons";

import { Icon } from "@/components/Icon/Icon";
import { getComposedEventTarget } from "@/utils/getComposedEventTarget";

const MENU_WIDTH = 360;

export interface CampaignErrorsChipProps {
	errors: string[];
	onRemoveDisconnected: () => void;
}

/**
 * CampaignErrorsChip — compact validation chip for the campaign-builder header.
 *
 * Shows the first error plus a "+N" badge; clicking it opens a portal dropdown
 * listing every error (mirrors the shared Dropdown portal pattern).
 */
export const CampaignErrorsChip: FunctionComponent<CampaignErrorsChipProps> = ({
	errors,
	onRemoveDisconnected,
}) => {
	const { t } = useTranslation();

	const [showDropdown, setShowDropdown] = useState<boolean>(false);
	const [position, setPosition] = useState<{ top: number; left: number } | null>(null);

	const triggerRef = useRef<HTMLDivElement>(null);
	const menuRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		const handleClick = (event: MouseEvent) => {
			const target = getComposedEventTarget(event);

			const insideTrigger = triggerRef.current && triggerRef.current.contains(target as Node);
			const insideMenu = menuRef.current && menuRef.current.contains(target as Node);

			if (!insideTrigger && !insideMenu && showDropdown) {
				setShowDropdown(false);
			}
		};

		window.addEventListener("click", handleClick);

		return () => window.removeEventListener("click", handleClick);
	}, [showDropdown]);

	useEffect(() => {
		if (!showDropdown) {
			return;
		}

		const updatePosition = () => {
			if (triggerRef.current) {
				const rect = triggerRef.current.getBoundingClientRect();
				const left =
					rect.left + MENU_WIDTH > window.innerWidth ? rect.right - MENU_WIDTH : rect.left;

				setPosition({ top: rect.bottom + 6, left });
			}
		};

		updatePosition();

		window.addEventListener("scroll", updatePosition, true);
		window.addEventListener("resize", updatePosition);

		return () => {
			window.removeEventListener("scroll", updatePosition, true);
			window.removeEventListener("resize", updatePosition);
		};
	}, [showDropdown]);

	const displayErrors = errors.length ? errors : [t("campaign.builder.invalidCampaign")];

	return (
		<>
			<div
				ref={triggerRef}
				className="inline-flex w-fit items-center gap-2 whitespace-nowrap rounded-[10px] border border-status-error/20 bg-status-error-light px-2.5 py-1.5 text-xs font-medium text-status-error cursor-pointer"
				onClick={() => setShowDropdown(!showDropdown)}
			>
				<Icon className="w-3.5 h-3.5" icon={faCircleExclamation} />
				<span className="max-w-[320px] overflow-hidden text-ellipsis">{displayErrors[0]}</span>
				{displayErrors.length > 1 && (
					<span className="flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-status-error px-1 text-[11px] font-bold text-white">
						+{displayErrors.length - 1}
					</span>
				)}
			</div>

			{showDropdown &&
				ReactDOM.createPortal(
					<div
						ref={menuRef}
						className="fixed z-[2000] w-[360px] rounded-lg border border-border-light bg-white p-1.5 shadow-lg"
						style={position ?? { visibility: "hidden" }}
					>
						<div className="mb-1 flex items-center justify-between border-b border-border-light px-2 pb-2 pt-1.5">
							<span className="text-[11px] font-bold uppercase tracking-wider text-text-disabled">
								{t("campaign.builder.errorsToFix", { count: displayErrors.length })}
							</span>
						</div>
						{displayErrors.map((error) => (
							<div key={error} className="flex flex-row items-start gap-2.5 rounded-md px-2.5 py-2">
								<Icon
									className="mt-0.5 w-3.5 h-3.5 flex-shrink-0 text-status-error"
									icon={faCircleExclamation}
								/>
								<div className="flex-1 text-sm text-text">{error}</div>
								{error.includes("remove them") && (
									<button
										className="rounded-full bg-status-error px-2 py-0.5 text-xs font-semibold text-white hover:opacity-80"
										type="button"
										onClick={() => {
											setShowDropdown(false);
											onRemoveDisconnected();
										}}
									>
										{t("campaign.builder.remove")}
									</button>
								)}
							</div>
						))}
					</div>,
					document.body
				)}
		</>
	);
};
